import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Spinner, Alert, Badge, Button, ButtonGroup, Input, Label, FormGroup } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import AuditLog from '../../entities/AuditLog';

export const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Optional user lookup so the table can show "Name <email>" instead of
  // a bare UserId. We fetch once on mount and refresh alongside the
  // log list — cheap because this is an admin-only page.
  const [users, setUsers] = useState([]);
  const [usersById, setUsersById] = useState({});

  // Filter state
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [messageSearch, setMessageSearch] = useState('');
  const [timestampFrom, setTimestampFrom] = useState('');
  const [timestampTo, setTimestampTo] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshIntervalRef = useRef(null);

  // Pagination
  const [offset, setOffset] = useState(0);
  const [limit] = useState(100);

  const availableTags = [
    { value: '', label: 'All Action Families' },
    { value: 'PackageTickers', label: 'Package Tickers' },
    { value: 'TradingStrategy', label: 'Trading Strategy' },
  ];

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiClient.searchUsers({ Limit: 10000, Offset: 0, SkipTotalCount: true });
      const items = (data && data.Items) || [];
      setUsers(items);
      const map = {};
      for (const u of items) {
        map[u.Id] = u;
      }
      setUsersById(map);
    } catch (err) {
      console.warn('Failed to load users for audit log filter:', err);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const searchParams = {
        Offset: offset,
        Limit: limit,
        SkipTotalCount: false,
      };

      if (selectedTag) {
        searchParams.Tag = selectedTag;
      }

      if (selectedUserId) {
        const parsed = parseInt(selectedUserId, 10);
        if (!Number.isNaN(parsed)) {
          searchParams.UserId = parsed;
        }
      }

      if (messageSearch) {
        searchParams.MessageSearch = messageSearch;
      }

      if (timestampFrom) {
        const from = new Date(timestampFrom);
        if (!Number.isNaN(from.getTime())) {
          searchParams.TimestampFrom = from.toISOString();
        }
      }

      if (timestampTo) {
        const to = new Date(timestampTo);
        if (!Number.isNaN(to.getTime())) {
          searchParams.TimestampTo = to.toISOString();
        }
      }

      const res = await apiClient.postJson('/api/audit-logs/search', searchParams);
      const response = await res.json();
      setLogs((response.Items || []).map((item) => new AuditLog(item)));
      setTotalCount(response.TotalCount || 0);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [offset, limit, selectedTag, selectedUserId, messageSearch, timestampFrom, timestampTo]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadLogs();
      }, 10000);
      refreshIntervalRef.current = interval;

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      };
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [autoRefresh, loadLogs]);

  const getTagBadgeColor = (tag) => {
    if (!tag) return 'secondary';
    if (tag.includes('PackageTickers')) return 'primary';
    if (tag.includes('TradingStrategy')) return 'success';
    return 'secondary';
  };

  const getUserLabel = (userId) => {
    if (usersById && usersById[userId]) {
      const u = usersById[userId];
      const name = u.Name || u.Email || `User #${userId}`;
      return `${name} (#${userId})`;
    }
    return `User #${userId}`;
  };

  const handlePreviousPage = () => {
    setOffset(Math.max(0, offset - limit));
  };

  const handleNextPage = () => {
    if (offset + limit < totalCount) {
      setOffset(offset + limit);
    }
  };

  const handleClearFilters = () => {
    setSelectedTag('');
    setSelectedUserId('');
    setMessageSearch('');
    setTimestampFrom('');
    setTimestampTo('');
    setOffset(0);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Audit Logs</h2>
        <div>
          <Button color="primary" onClick={loadLogs} disabled={loading} className="me-2">
            Refresh
          </Button>
          <FormGroup check inline className="me-3">
            <Label check>
              <Input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              {' '}Auto-refresh (10s)
            </Label>
          </FormGroup>
        </div>
      </div>

      <p className="text-muted">
        Audit entries are immutable. Each row records a user-driven admin action
        (Tag identifies the action family). Entries cannot be removed from the UI.
      </p>

      {/* Filters */}
      <div className="mb-3 p-3 border rounded">
        <div className="row">
          <div className="col-md-3">
            <FormGroup>
              <Label for="tagFilter">Filter by Action Family</Label>
              <Input
                type="select"
                id="tagFilter"
                value={selectedTag}
                onChange={(e) => {
                  setSelectedTag(e.target.value);
                  setOffset(0);
                }}
              >
                {availableTags.map(tag => (
                  <option key={tag.value} value={tag.value}>{tag.label}</option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <Label for="userFilter">Filter by User</Label>
              <Input
                type="select"
                id="userFilter"
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All Users</option>
                {users.map(u => (
                  <option key={u.Id} value={u.Id}>
                    {u.Name || u.Email || `User #${u.Id}`} (#{u.Id})
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <Label for="messageSearch">Message Contains</Label>
              <Input
                type="text"
                id="messageSearch"
                placeholder="e.g. strategy #12"
                value={messageSearch}
                onChange={(e) => {
                  setMessageSearch(e.target.value);
                  setOffset(0);
                }}
              />
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <Label>&nbsp;</Label>
              <div>
                <Button color="secondary" onClick={handleClearFilters} disabled={loading}>
                  Clear Filters
                </Button>
              </div>
            </FormGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <FormGroup>
              <Label for="timestampFrom">From</Label>
              <Input
                type="datetime-local"
                id="timestampFrom"
                value={timestampFrom}
                onChange={(e) => {
                  setTimestampFrom(e.target.value);
                  setOffset(0);
                }}
              />
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <Label for="timestampTo">To</Label>
              <Input
                type="datetime-local"
                id="timestampTo"
                value={timestampTo}
                onChange={(e) => {
                  setTimestampTo(e.target.value);
                  setOffset(0);
                }}
              />
            </FormGroup>
          </div>
        </div>
      </div>

      {error && <Alert color="danger" fade={false}>{error}</Alert>}

      {loading && <Spinner color="primary" />}

      {!loading && logs.length === 0 && (
        <Alert color="info" fade={false}>No audit logs found</Alert>
      )}

      {!loading && logs.length > 0 && (
        <>
          <div className="mb-2">
            <strong>Total entries: {totalCount}</strong>
            {' - '}
            Showing {offset + 1} to {Math.min(offset + limit, totalCount)}
          </div>

          <div className="mb-3">
            <ButtonGroup>
              <Button
                color="secondary"
                size="sm"
                onClick={handlePreviousPage}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleNextPage}
                disabled={offset + limit >= totalCount}
              >
                Next
              </Button>
            </ButtonGroup>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Timestamp</th>
                  <th style={{ width: '200px' }}>Action Family</th>
                  <th style={{ width: '220px' }}>User</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.Id}>
                    <td>{log.getFormattedTimestamp()}</td>
                    <td>
                      <Badge color={getTagBadgeColor(log.Tag)}>
                        {log.Tag}
                      </Badge>
                    </td>
                    <td>{getUserLabel(log.UserId)}</td>
                    <td>
                      <div style={{ maxWidth: '600px' }}>
                        <div>{log.Message}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="mb-3">
            <ButtonGroup>
              <Button
                color="secondary"
                size="sm"
                onClick={handlePreviousPage}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={handleNextPage}
                disabled={offset + limit >= totalCount}
              >
                Next
              </Button>
            </ButtonGroup>
          </div>
        </>
      )}
    </div>
  );
};
